import type { TripItineraryData, TripItineraryItem, TripItemWithoutDay } from 'tripPlanner/api/types'
import {
  hasValidLatLng,
  MARKER_TYPE_STOP,
  MARKER_TYPE_ITEM,
  MARKER_TYPE_CHAT_HOTEL,
  MARKER_TYPE_CHAT_EXPERIENCE,
  MARKER_SOURCE_ITINERARY,
  type MapMarkerComponent,
} from 'tripPlanner/utils/mapMarkerTransforms'
import { isExperienceSavedData, isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'

type ItineraryItem = TripItineraryItem | TripItemWithoutDay
type MapMarker = MapMarkerComponent

export function getItemCoords(item: ItineraryItem): { lat: number; lng: number } | undefined {
  if (item.type === 'ATTRACTION' && item.isPlaceLookupApplicable === false) return undefined
  if (item.itemPlaceDetail && hasValidLatLng(item.itemPlaceDetail.lat, item.itemPlaceDetail.lng)) {
    return { lat: item.itemPlaceDetail.lat, lng: item.itemPlaceDetail.lng }
  }
  const experienceData = isExperienceSavedData(item.savedItemData) ? item.savedItemData : undefined
  const experienceGeo = experienceData?.location?.geoLocation
  if (experienceGeo && hasValidLatLng(experienceGeo.lat, experienceGeo.lng)) {
    return { lat: experienceGeo.lat, lng: experienceGeo.lng }
  }
  const offerData = isHotelSavedData(item.savedItemData) ? item.savedItemData : undefined
  const geo = offerData?.location?.geoLocation
  if (geo && hasValidLatLng(geo.lat, geo.lng)) {
    return { lat: geo.lat, lng: geo.lng }
  }
  return undefined
}

function isAccommodationWithOffer(item: ItineraryItem): boolean {
  return item.type === 'ACCOMMODATION' && isHotelSavedData(item.savedItemData) && Boolean(item.savedItemData.offerId)
}

export function buildItemMarker(
  item: ItineraryItem,
  coords: { lat: number; lng: number },
  stopId: number,
  dayId?: number,
): MapMarker {
  const isHotel = isAccommodationWithOffer(item)
  const experienceData = isExperienceSavedData(item.savedItemData) ? item.savedItemData : undefined
  const isExperience = item.type === 'EXPERIENCE' && typeof experienceData?.experienceId === 'string'
  const offerId = isHotelSavedData(item.savedItemData)
    ? item.savedItemData.offerId
    : isExperience
      ? experienceData?.experienceId
      : undefined
  return {
    lat: coords.lat,
    lng: coords.lng,
    itemId: item.id,
    stopId,
    dayId,
    markerId: offerId ?? `item-${item.id}`,
    label: item.name,
    description: item.description,
    placeId: item.itemPlaceDetail?.placeId,
    markerType: isHotel && offerId
      ? MARKER_TYPE_CHAT_HOTEL
      : isExperience && offerId
        ? MARKER_TYPE_CHAT_EXPERIENCE
        : MARKER_TYPE_ITEM,
    source: MARKER_SOURCE_ITINERARY,
    ...((isHotel || isExperience) && offerId ? { offerId } : {}),
  }
}

/**
 * Extracts itinerary markers (stops and items) from itinerary data.
 * Items with type ACCOMMODATION and savedItemData.offerId are emitted as chat-hotel markers
 * so they show the hotel icon and new add-to-trip hover UI.
 *
 * Also returns unresolvedHotels: accommodation items that have an offerId but no
 * coordinates. The merge logic uses these to upgrade matching location_entities
 * markers from suggested -> chat-hotel so they still get the hotel icon.
 */
export function extractItineraryMarkers(itineraryData: TripItineraryData | undefined): {
  markers: Array<MapMarker>
  unresolvedHotels: Map<string, string>
} {
  const empty = { markers: [], unresolvedHotels: new Map<string, string>() }
  const stops = itineraryData?.stops ?? []
  if (stops.length === 0) return empty

  const allMarkers: Array<MapMarker> = []
  const unresolvedHotels = new Map<string, string>()
  const seenItemIds = new Set<number>()

  const processItem = (item: ItineraryItem, stopId: number, dayId?: number) => {
    if (seenItemIds.has(item.id)) return
    seenItemIds.add(item.id)

    const coords = getItemCoords(item)
    if (coords) {
      allMarkers.push(buildItemMarker(item, coords, stopId, dayId))
    } else if (item.type === 'ACCOMMODATION' && isHotelSavedData(item.savedItemData) && item.savedItemData.offerId) {
      unresolvedHotels.set(item.name.toLowerCase(), item.savedItemData.offerId)
    }
  }

  stops.forEach((stop) => {
    if (
      stop.stopPlaceDetail &&
      hasValidLatLng(stop.stopPlaceDetail.lat, stop.stopPlaceDetail.lng)
    ) {
      allMarkers.push({
        lat: stop.stopPlaceDetail.lat,
        lng: stop.stopPlaceDetail.lng,
        markerId: `stop-${stop.id}`,
        stopId: stop.id,
        label: stop.placeName,
        placeId: stop.stopPlaceDetail.placeId,
        markerType: MARKER_TYPE_STOP,
        source: MARKER_SOURCE_ITINERARY,
        isOriginStop: stop.stopType === 'ORIGIN',
      })
    }

    stop.days.forEach((day) => {
      day.items.forEach((item) => processItem(item, stop.id, day.id))
    })

    stop.itemsWithoutDay?.forEach((item) => processItem(item, stop.id))
  })

  return { markers: allMarkers, unresolvedHotels }
}
